import type { RouteLocationNormalizedLoaded, RouteLocationRaw, Router } from 'vue-router';
import { defineStore } from 'pinia';
import { Organisation, UNKNOWN_ORGANISATION } from '@/api/model/Organisation';

interface State {
    isFetchingOrganisations: boolean;
    organisations: Organisation[];
    current_organisation: Organisation;
    loading: boolean;
}

const ORG_STORAGE_KEY = 'organisation';

/**
 * Stands in for "no organisation is active": before a navigation settles one,
 * and when the user belongs to none at all. Its id is 0, which is how callers
 * tell it apart from a real membership.
 *
 * Deliberately not seeded from storage. The URL decides which organisation is
 * active, and a placeholder wearing the last-seen id would look like a decision
 * that nothing had actually taken.
 *
 * Built through fromJSON rather than as a literal so it is a real Organisation
 * -- callers get the model's behaviour (hasPermission and friends) instead of a
 * shape that merely type-checks. Empty timestamps fall back to now.
 */
function placeholderOrganisation(): Organisation {
    return Organisation.fromJSON({
        public_id: UNKNOWN_ORGANISATION,
        name: '',
        logo: null,
        base_currency: null,
        vat_number: null,
        company_registration_number: null,
        registered_office_address: null,
        created_at: '',
        updated_at: '',
        oldest_order_date: null,
        permissions: [],
    });
}

/**
 * Where the picker should land when switching to `organisation`.
 *
 * Staying on the same screen is the point of a switcher, but only the screens
 * that are about the organisation as a whole survive the move. Anything
 * identifying a record -- a supplier, a purchase order -- names one belonging
 * to the organisation being left, which does not exist in the one being
 * entered, so those go to the dashboard instead.
 */
function switchTarget(
    route: RouteLocationNormalizedLoaded,
    organisation: Organisation,
    router: Router,
): RouteLocationRaw {
    const dashboard = { name: 'dashboard', params: { organisation: organisation.public_id } };
    const identifiesARecord = Object.keys(route.params).some((param) => param !== 'organisation');

    if (identifiesARecord) {
        return dashboard;
    }

    const here = {
        name: route.name,
        params: { ...route.params, organisation: organisation.public_id },
        query: route.query,
    };

    /*
     * Some routes under this layout carry no organisation in their path -- the
     * profile page has one injected into its params without appearing in the
     * URL. Swapping it there resolves to the URL we are already on, which
     * vue-router treats as a duplicate and skips, so the switch would do
     * nothing.
     */
    return router.resolve(here).fullPath === route.fullPath ? dashboard : here;
}

export const useOrganisationStore = defineStore('organisation', {
    state: (): State => ({
        isFetchingOrganisations: false,
        organisations: [],
        current_organisation: placeholderOrganisation(),
        loading: false,
    }),
    getters: {
        /**
         * Whether a real membership is active, as opposed to the placeholder
         * that stands in before a navigation settles one.
         *
         * Checked by membership rather than by inspecting the placeholder, so
         * nothing depends on the shape of the stand-in.
         */
        hasActiveOrganisation(): boolean {
            return this.organisations.some(
                (organisation: Organisation) => organisation.public_id === this.current_organisation.public_id,
            );
        },
    },
    actions: {
        /**
         * Record the memberships. Deliberately does not pick one: the route
         * decides, and `organisationGuard` applies it once this has run.
         */
        setOrganisations(organisations: Organisation[]): Organisation[] {
            this.organisations = organisations;

            if (organisations.length === 0) {
                // Nothing can be active. Leaving the previous value would hand
                // whoever signs in next the last account's organisation, and
                // organisation-keyed queries would answer from cache before the
                // server ever refused them.
                this.current_organisation = placeholderOrganisation();
            }

            return organisations;
        },

        /**
         * Record an organisation the user has just been made a member of.
         *
         * Separate from `setOrganisations`, which replaces the list as the
         * profile reports it. Creating one adds a membership the profile has
         * not been re-read since, and the router refuses to navigate into an
         * organisation it cannot find here -- so this has to land before the
         * push to the new dashboard.
         */
        addOrganisation(organisation: Organisation): void {
            if (this.findOrganisationByPublicId(organisation.public_id)) {
                return;
            }

            this.organisations.push(organisation);
        },

        /**
         * Forget which organisation was open. Called when the session ends, so
         * nothing carries into the next one in the same tab.
         */
        reset(): void {
            this.organisations = [];
            this.current_organisation = placeholderOrganisation();
        },

        /**
         * Make an organisation the active one.
         *
         * The only writer of `current_organisation`, called only by
         * `organisationGuard` -- the URL decides which organisation is active,
         * and this applies that decision. Storage is written here purely as a
         * record of the last one seen, for the next visit that names none.
         */
        useOrganisation(organisation: Organisation): void {
            this.current_organisation = organisation;
            localStorage.setItem(ORG_STORAGE_KEY, organisation.public_id);
        },

        /**
         * The last organisation this browser had open, if the user still
         * belongs to it. Only consulted when the URL names none -- it is a
         * hint, never the source, and it is shared by every tab on the origin.
         */
        lastSeenOrganisation(): Organisation | undefined {
            const stored = localStorage.getItem(ORG_STORAGE_KEY);

            return stored ? this.findOrganisationByPublicId(stored) : undefined;
        },

        /**
         * Where to send someone who has not asked for a particular
         * organisation: the last one they had open, else any membership.
         *
         * `current_organisation` cannot answer this. It reflects the URL, so
         * before the first navigation -- registering, say -- it is still the
         * placeholder. Callers building a destination need a real membership.
         */
        defaultOrganisation(): Organisation | undefined {
            return this.lastSeenOrganisation() ?? this.organisations[0];
        },

        /**
         * Switch organisation, from the picker in the sidebar or header.
         *
         * Navigates rather than assigning: the URL is what decides which
         * organisation is active, so changing it is how you switch, and
         * `organisationGuard` then applies it. Assigning here as well would
         * make two writers of the same thing again.
         */
        setCurrentOrganisation(org: Organisation | undefined): void {
            if (!org) {
                return;
            }

            const route = this.router.currentRoute.value;

            if (route.params['organisation'] === org.public_id) {
                return;
            }

            this.router.push(switchTarget(route, org, this.router));
        },

        findOrganisationByPublicId(publicId: string): Organisation | undefined {
            return this.organisations.find((org: Organisation) => org.public_id === publicId);
        },
    },
});
